/**
 * A generic **overflow action menu** — a labelled trigger and a grouped `role="menu"` popup.
 *
 * The owner, on the data-plane screen: *"I want the actions to be more hidden and not upfront."* That
 * screen renders ~14 lifecycle controls inline in four labelled groups, above the diagnostic content an
 * operator needs in order to decide whether to act at all — a control panel with the switches above the
 * gauges. This is where they went.
 *
 * ⚠️ History: the data-plane screen (`DataPlaneActions`) moved to the WARD `DropdownMenu` on
 * 2026-10-08 (console WARD slice 7). The adopters today are `NavRail` and the channel and group
 * panes; the data-plane notes below record why the contract has its shape.
 *
 * ## ⭐ THE MECHANICS ARE IMPORTED FROM `UserMenu`, NOT RE-IMPLEMENTED
 *
 * `classifyMenuKey`, `menuItemsIn`, `focusMenuItem` and `isOutside` are already exported there, already
 * at 100%, and already carry the decisions this component would otherwise re-derive and get subtly
 * different. ⛔ Two menus in one package that disagree about what `Tab` does is a worse outcome than
 * either duplication or a refactor. `focusPanel` likewise comes from `dialog-panel`.
 *
 * ## ⛔ THE THREE TRAPS THIS COMPONENT EXISTS TO NOT FALL INTO
 *
 * 1. **CONDITIONALLY MOUNTED, never rendered-and-`hidden`.** `getByTestId` **ignores `hidden`**, so a
 *    hidden-but-present menu satisfies every *"can the operator reach this?"* assertion while being
 *    unreachable. Unmounting makes the closed state observable to role queries, which respect it.
 *    ⇒ tests must assert the CLOSED state first: a suite that only ever looks at the open menu passes
 *    against a menu stuck open, and open is the state an author eyeballs while building.
 * 2. **Focus restore must not fire on mount.** `prevOpen` gates it on a real open→close transition. An
 *    effect keyed on `[open]` that focuses the trigger unconditionally steals focus the moment the page
 *    renders and scrolls the overflow container to it — a defect this package has already shipped once.
 * 3. ⛔ **`onOpenDialog` suppresses the restore for exactly one close.** Every destructive action here
 *    opens a confirmation, so the menu closes *in order to hand focus to a dialog*; restoring focus to
 *    the trigger in that transition rips it back out of the dialog that just opened.
 *
 * ## ⛔ A STATE-DISABLED ITEM USES `aria-disabled`, NOT `disabled` (and this is a RULING)
 *
 * The data-plane screen's contract is *"state-gated ⇒ RENDERED, disabled, with the reason on the
 * control, reachable via `aria-describedby`"*. ⛔ Inside a menu the `disabled` **attribute** defeats
 * that contract: a disabled element is not focusable, so arrow-key navigation cannot land on it and the
 * reason becomes unreachable by exactly the operator the reason was written for — while
 * `menuItemsIn` still *finds* it, so arrowing appears to stick on the previous item. `aria-disabled`
 * keeps the item focusable, announces the state, and lets the reason be read. Activation is guarded in
 * the handler instead of by the platform.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { focusPanel, isImeKey, offerDialogOpener, withdrawDialogOpener } from '../panes/dialog-panel';
import {
  classifyMenuKey,
  focusMenuItem,
  isOutside,
  isTypeAheadKey,
  menuItemsIn,
  typeAheadIndex,
} from './UserMenu';
import { Button, type ButtonTone } from './Button';
import { actionToneClass } from './data-tone';
import { FIELD_CLASS } from './FilterSection';

/*
 * ⛔ A CONTEXT rather than a cloned prop. An item deep inside a group needs `closeForDialog`, and both
 * alternatives are worse: `React.cloneElement` over `children` silently overrides whatever the caller
 * set (including a `disabled` the ruling in this file's header forbids), and threading it through every
 * group as a prop makes the group a courier for something it has no opinion about.
 */
const DialogCloseContext = createContext<(() => void) | null>(null);

/**
 * For a menuitem that opens a dialog: closes the menu WITHOUT restoring focus to the trigger.
 *
 * ⛔ Returns a no-op outside a menu rather than throwing. An item rendered both inside the menu and on a
 * card (the data-plane screen does exactly that with `restart-operator`) must not crash in the second
 * position, and a `null` return would push that same check into every call site.
 */
export function useCloseMenuForDialog(): () => void {
  const close = useContext(DialogCloseContext);
  return close ?? (() => undefined);
}

/* The plain close of the menu that holds the caller. Separate from the dialog close above. */
const MenuCloseContext = createContext<(() => void) | null>(null);

/**
 * For a menuitem that acts in place, such as a link row of the rail picker: closes the menu and gives
 * focus back to the trigger, as Escape does.
 *
 * - Inside an `ActionMenu`: the returned function closes that menu. Focus goes to the trigger on the
 *   close, because no dialog takes it.
 * - Outside a menu: the returned function does nothing, for the same reason as
 *   {@link useCloseMenuForDialog}.
 *
 * It changes no route and calls no item handler. A link row still navigates through its own `href`.
 */
export function useCloseMenu(): () => void {
  const close = useContext(MenuCloseContext);
  return close ?? (() => undefined);
}

/**
 * Where the popup hangs, and how wide it is.
 *
 * ⛔ **A CLOSED UNION OF TWO NAMED INTENTS, NOT A `className` ESCAPE HATCH.** `Button` records why
 * (*"a forwardable `className` is how 'one definition' decays back into 86"*) and a popup is no
 * different: the first caller that needs a tweak adds `menuClassName` and every later one copies it.
 *
 * - `end` — the overflow-menu geometry: fixed `w-80`, right edge aligned to the trigger. Correct for a
 *   toolbar button in a wide pane, where the menu is much wider than its trigger.
 * - `stretch` — the picker geometry: left edge aligned to the trigger, **at least** as wide as it, free
 *   to grow to its content up to a cap. With `search` set, the frame has the fixed `w-72`. ⛔ `end`'s fixed `w-80` (320px) is not merely wrong inside the
 *   240px rail, it is off-screen: `right-0` anchors the right edge to a 216px container and the other
 *   320px extend LEFT, past x=0.
 */
export type ActionMenuPlacement = 'end' | 'stretch';

export interface ActionMenuProps {
  /**
   * The trigger's accessible name.
   *
   * ⚠️ Also its visible text UNLESS {@link triggerContent} is supplied — in which case this becomes an
   * `aria-label` and the content carries the pixels. ⛔ It stays REQUIRED either way: a composite
   * trigger's accessible name computed from its children is whatever the children happen to concatenate
   * to, which for the rail chip is `"CR Crawler crawler"`. One deliberate string beats that.
   */
  readonly label: string;
  /**
   * Rich trigger content, for a trigger that is not a word.
   *
   * ⛔ **ADDED RATHER THAN FORKING THIS COMPONENT** (owner, 2026-08-07: *"I would expect that dropdown
   * to be an actual dropdown"*). The rail's app chip is a 26px monogram plus a stacked name and slug —
   * `label: string` cannot express it, and the alternatives were both worse: a second menu beside this
   * one duplicates all three focus traps and the keyboard arithmetic, and a hand-rolled `<button>`
   * would add an offender to `one-button-definition.test.ts`, a lowerable-only ratchet at 54.
   *
   * ⚠️ The chevron is NOT part of this — it stays owned by the component, because it is the popup
   * affordance and every trigger here has a popup. That is the whole defect being fixed: the chip
   * shipped with a chevron and a plain navigation behind it, so it *signified* a menu and *delivered* a
   * link.
   */
  readonly triggerContent?: ReactNode;
  /** Forwarded to {@link Button}. ⚠️ `chrome` is the one to use in the rail or top bar — see its tone. */
  readonly tone?: ButtonTone;
  /** @see ActionMenuPlacement — defaults to `end`, the overflow-menu geometry this file started as. */
  readonly placement?: ActionMenuPlacement;
  /** The popup's accessible name. ⚠️ Distinct from {@link label}: the trigger says what it opens. */
  readonly menuLabel: string;
  readonly id: string;
  readonly testId: string;
  /**
   * The menuitems — use {@link ActionMenuItem}, optionally inside `role="group"` elements.
   *
   * ⚠️ Each item must carry `role="menuitem"` and `tabIndex={-1}` itself — this component finds them by
   * role and drives focus, but it does not clone or rewrite its children. Cloning would make the item's
   * own props silently overridable, which is how a `disabled` sneaks back in past the ruling above.
   * ⇒ {@link ActionMenuItem} exists so that requirement is satisfied in ONE place rather than
   * re-derived per caller; see its docblock.
   */
  readonly children: ReactNode;
  /**
   * Called after the menu closes because an item opened a dialog. ⛔ Its existence is what tells this
   * component to skip the focus restore for that one close — see trap 3.
   */
  readonly onDialogOpened?: () => void;
  /**
   * A filter field above the menu, for a list too long to scan. The caller owns the text and filters
   * its own children, so this component does not know what a row is.
   *
   * The `<input>` is a sibling of the `role="menu"` element and never its child. A `menu` may hold
   * only `menuitem`, `group` and `separator`, and a row still navigates, so `menu` is the true role.
   * Open focuses the field and not the first row. Every close tells the caller the empty string.
   */
  readonly search?: ActionMenuSearch;
}

/** The filter field of an {@link ActionMenu}. The caller holds the text. */
export interface ActionMenuSearch {
  readonly value: string;
  /** Told the whole new text on every keystroke, and told the empty string on every close. */
  readonly onChange: (next: string) => void;
  /** The field's accessible name and placeholder, for example `Filter apps`. */
  readonly label: string;
  /** The polite status, for example `2 apps` or `No apps match`. The empty string says nothing. */
  readonly status: string;
  /** True when the filter left no caller row. The status is then visible, and Enter in the field does nothing. */
  readonly empty: boolean;
}

/**
 * How an item READS. ⛔ Two values, and it is the VISUAL tone only — never the domain's own severity.
 *
 * `DataPlaneActions` had three severities (`normal` / `irreversible` / `no-recovery`) and only two
 * treatments, because the third distinction does placement work rather than colour work. ⇒ the domain
 * value travels as a `data-*` attribute through {@link ActionMenuItemProps.attrs}, where a test can
 * assert it, and this prop decides only how the row is painted. Collapsing the two would either lose
 * `data-tone="no-recovery"` or invent a third colour nobody designed.
 */
export type ActionMenuItemTone = 'normal' | 'danger';

/**
 * Why an item cannot be activated right now — **or `null`, which is the ordinary case.**
 *
 * ⛔ **The reason and its id are NESTED rather than two sibling props, and that is the design.** Two
 * siblings can disagree: an item with `reasonId` and no reason, or a reason with no id to point
 * `aria-describedby` at, are both representable and both silently break the announcement. Nesting makes
 * them unrepresentable — the same argument `ScrollTable.spanningRow` records for the same reason.
 *
 * ⚠️ There is deliberately no `kind` field. `ScrollTable`'s `BLOCK_KINDS` has two members, but inside a
 * menu **permission HIDES** (see {@link ActionMenuItem}), so `state` is the only kind that can reach
 * this type. A parameter with one reachable value is a decision the reader has to re-derive; the
 * attribute is stamped as `state` and the reason is written here instead.
 */
export interface ActionMenuItemBlock {
  /** The sentence an operator reads. ⛔ Rendered visibly AND wired to `aria-describedby`. */
  readonly reason: string;
  /**
   * The DOM id the reason node carries.
   *
   * ⛔ **Required, never derived from `testId`.** `DataPlaneActions` shipped `dp-action-<id>` on the
   * control and `dp-action-why-<id>` on the reason, and both are pinned in tests **outside** that file
   * — a derived default would have renamed one of them on migration. Callers say what the id is.
   */
  readonly reasonId: string;
}

export interface ActionMenuItemProps {
  /** What the operator reads on the row. A node, so an icon can sit beside the verb. */
  readonly label: ReactNode;
  readonly tone: ActionMenuItemTone;
  /** ⛔ `null` when the action is available. See {@link ActionMenuItemBlock}. */
  readonly blocked: ActionMenuItemBlock | null;
  /**
   * Runs on activation. ⛔ NOT called while {@link blocked} — the guard is in the handler, because the
   * `disabled` attribute is forbidden here (see this file's header ruling).
   *
   * ⚠️ The menu is closed FIRST, without restoring focus to the trigger, because essentially every
   * caller opens a dialog. See {@link useCloseMenuForDialog} and trap 3.
   */
  readonly onSelect: () => void;
  readonly testId: string;
  /**
   * Extra `data-*` attributes for the caller's own domain facts — `data-tone`, `data-group`.
   *
   * ⛔ **Spread FIRST, so this component's own attributes always win.** `role`, `tabIndex`,
   * `aria-disabled`, `aria-describedby` and `data-blocked-by` are the contract this component exists to
   * guarantee; a caller must not be able to overwrite one through here, which is exactly the hazard
   * `ActionMenuProps.children` refuses `cloneElement` over.
   *
   * ⚠️ Optional, and spread with no `?? {}` fallback: spreading `undefined` is already a no-op, so a
   * fallback would add a branch that can never behave differently.
   */
  readonly attrs?: Record<string, string | undefined>;
}

/** Shared by both tones. ⛔ `aria-disabled:` variants, never `disabled:` — see the header ruling. */
const ITEM_BASE_CLASS =
  'flex w-full items-center px-3 py-2 text-left text-sm transition-colors ' +
  'aria-disabled:cursor-not-allowed aria-disabled:text-muted ' +
  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring';

/**
 * ⛔ Whole class strings in a lookup, and the JSX interpolates nothing — for the reason
 * {@link MENU_PLACEMENT_CLASS} records. ⚠️ **Its SECOND reason (`prose-measure.test.ts` ratchet A)
 * expired on 2026-08-08** when that scanner learned to balance `{}`; the readability reason is the
 * one that survives, and it is sufficient. See that docblock for the correction.
 *
 * ⛔ **The COLOUR comes from `shell/data-tone`'s {@link actionToneClass}, not from a literal here.**
 * The two literals it replaces were byte-identical to `UserMenu.tsx:463`'s hand-written sign-out row
 * — one decision, two independent authors, which is the defect `data-tone.tsx` exists to end.
 * ⚠️ Composition still happens at module scope, so Tailwind sees the literals in `data-tone.tsx` and
 * the JSX below still interpolates nothing: this map is what `className` reads, whole.
 */
const ITEM_TONE_CLASS: Record<ActionMenuItemTone, string> = {
  normal: `${ITEM_BASE_CLASS} ${actionToneClass('normal')}`,
  danger: `${ITEM_BASE_CLASS} ${actionToneClass('danger')}`,
};

/**
 * **One menuitem** — the whole `role="menuitem"` contract, in one place.
 *
 * ## ⛔ WHY THIS EXISTS: THE CONTRACT WAS RE-DERIVED PER CALLER
 *
 * {@link ActionMenuProps.children} says each item must carry `role="menuitem"` and `tabIndex={-1}`
 * itself, and deliberately refuses to clone children to add them. That is right, and it left every
 * caller hand-rolling a `<button className=…>` with five things to get right —
 * `role` · `tabIndex={-1}` · `aria-disabled` (**never** `disabled`) · `aria-describedby` pointing at a
 * visible reason · closing the menu WITHOUT restoring focus. Miss any one and the failure is silent:
 * arrow keys stick, or a reason is announced to nobody.
 *
 * ⭐ It also removes the last reason for a pane to hand-roll a `<button>`. Three ratchets count those
 * (`primitive-adoption`'s two and `one-button-definition`), all lowerable-only, and *"use `ActionMenu`"*
 * and *"no hand-rolled buttons"* were flatly contradictory instructions until this existed.
 *
 * ## ⛔ PERMISSION HIDES; STATE DISABLES
 *
 * A control the operator may not use is **absent** — render nothing, do not render this blocked. A door
 * that 403s is worse than no door, and a permission gate rendered as a greyed row invites a support
 * ticket for a capability the operator was never going to get. ⇒ `blocked` is for STATE only, and
 * `data-blocked-by` is stamped `state` unconditionally.
 */
export function ActionMenuItem({
  label,
  tone,
  blocked,
  onSelect,
  testId,
  attrs,
}: ActionMenuItemProps): React.JSX.Element {
  const closeForDialog = useCloseMenuForDialog();
  return (
    <>
      <button
        {...attrs}
        type="button"
        role="menuitem"
        tabIndex={-1}
        onClick={() => {
          /* ⛔ The guard the forbidden `disabled` attribute would otherwise have provided. */
          if (blocked !== null) return;
          closeForDialog();
          onSelect();
        }}
        aria-disabled={blocked !== null}
        aria-describedby={blocked === null ? undefined : blocked.reasonId}
        data-testid={testId}
        data-blocked-by={blocked === null ? undefined : 'state'}
        className={ITEM_TONE_CLASS[tone]}
      >
        {label}
      </button>
      {/* ⛔ VISIBLE, not `sr-only`. A sighted operator staring at a row that will not respond needs the
          same sentence the screen reader gets, and `aria-describedby` above points here. */}
      {blocked !== null && (
        <span
          id={blocked.reasonId}
          data-testid={blocked.reasonId}
          className="block px-3 pb-1.5 text-xs text-secondary"
        >
          {blocked.reason}
        </span>
      )}
    </>
  );
}

/**
 * ⛔ The two geometries, as whole class strings rather than composed fragments — so a reader sees each
 * popup exactly as it renders instead of reconstructing it from conditionals.
 *
 * `stretch` is `min-w-full w-max`: at least the trigger's width (so it never looks detached from a
 * 216px chip) and otherwise its content's, capped at `19rem` = 304px. It is allowed to overhang the
 * rail into the content area — that is ordinary dropdown behaviour, and the alternative is truncating
 * app names in a 216px column.
 */
const MENU_BASE_CLASS =
  'absolute top-full z-40 mt-1 max-h-[70vh] overflow-y-auto rounded-lg border border-border-subtle ' +
  'bg-surface-elevated py-1 shadow-lg';

/**
 * `shadow-lg` is the popup elevation, on `MENU_BASE_CLASS` and on each frame below. The fill alone
 * sits on the page at 1.05:1 in light and 1.20:1 in dark, so a popup over content read as part of it.
 *
 * The popup of a menu with a filter field: the frame holds the field in place and the menu scrolls
 * under it. Whole strings, as the lookup below.
 *
 * Each frame has a fixed width (`end` keeps its `w-80`, `stretch` takes `w-72`, as the deleted
 * `FilterMultiSelect` panel did) and no `w-max`. A width that follows the visible rows makes the panel
 * and the field shrink while the operator types.
 */
const SEARCH_FRAME_PLACEMENT_CLASS: Record<ActionMenuPlacement, string> = {
  end: 'absolute top-full z-40 mt-1 flex max-h-[70vh] flex-col overflow-hidden rounded-lg border border-border-subtle bg-surface-elevated shadow-lg right-0 w-80',
  stretch:
    'absolute top-full z-40 mt-1 flex max-h-[70vh] flex-col overflow-hidden rounded-lg border border-border-subtle bg-surface-elevated shadow-lg left-0 w-72',
};

/** The scrolling menu inside that frame. */
const SEARCH_MENU_CLASS = 'min-h-0 flex-1 overflow-y-auto py-1';

/** The filter field. `placeholder:text-muted` keeps its only visible label at AA contrast. */
const SEARCH_FIELD_CLASS = `w-full ${FIELD_CLASS} placeholder:text-muted`;

/**
 * ⛔ **WHOLE CLASS STRINGS, AND THE JSX MUST INTERPOLATE NOTHING** — two independent reasons, and the
 * second one is a gate.
 *
 * 1. A reader sees each popup exactly as it renders, instead of reconstructing it from conditionals.
 * 2. ⚠️⚠️ **THE GATE REASON IS OBSOLETE AS OF 2026-08-08 — corrected here rather than left to rot.**
 *    It read: *"`className={`… ${expr}`}` in JSX TRIPS `prose-measure.test.ts` RATCHET A … its element
 *    parser stops at the `=>`/`${` inside an attribute and releases the rest of the attribute list as
 *    element body text, so a long className is counted as 90 characters of unmeasured prose (artifact
 *    **B1**). Measured 2026-08-07: the template-literal form here took the count from 33 to 34 and red
 *    the gate."* ⭐ **All of that was true when written**, and it is exactly the artifact class that
 *    made §3 read 38 where the honest number was 22 — 14 of its 18 phantom offenders were `className`
 *    strings counted as prose. `elements()` and `literalText()` now balance `{}` and quotes to a tag's
 *    real closing `>`, so **a template-literal className no longer trips anything.**
 *
 *    ⇒ reason 1 stands alone and is enough; ⛔ **do not cite ratchet A for this shape, and do not
 *    assume the gate will catch you if you inline one.** ⚠️ The rule it ends with is still right for
 *    its own sake and is why the lookup below stays whole-string: *"do not raise a ceiling to
 *    accommodate a className."*
 */
const MENU_PLACEMENT_CLASS: Record<ActionMenuPlacement, string> = {
  end: `${MENU_BASE_CLASS} right-0 w-80`,
  stretch: `${MENU_BASE_CLASS} left-0 w-max min-w-full max-w-[19rem]`,
};

/**
 * ⛔⛔ **`stretch` STRETCHES THE TRIGGER TOO, AND THIS LINE IS A RENDER-CAUGHT DEFECT.**
 *
 * The first cut set only the popup's geometry and let the caller's own layout widen the trigger. It
 * does not work and **no test could have seen it**: `Button`'s base class is `inline-flex`, so the
 * element that a parent `flex flex-col` stretches is THIS container — the button inside it stays
 * content-sized. Measured in headless Chrome: container **215px**, trigger **~124px**, so the rail's
 * app chip rendered as a small pill with its chevron jammed against the app name instead of the
 * full-width header the owner had already approved. happy-dom computes no layout, so the suite was
 * green throughout.
 *
 * ⇒ `flex flex-col` here makes the button a flex item on a COLUMN axis, where `align-items: stretch`
 * resolves horizontally and fills the container. ⚠️ `flex-1` would be the row-axis answer and is not
 * available: `Button` refuses a forwarded `className` by design, which is exactly why the fix belongs
 * to the parent that `placement` already owns.
 */
const TRIGGER_PLACEMENT_CLASS: Record<ActionMenuPlacement, string> = {
  end: 'relative',
  stretch: 'relative flex flex-col',
};

/** Keys typed within this many milliseconds of the last one extend the type-ahead text (D9 of the G2 plan). */
const TYPEAHEAD_RESET_MS = 500;

export function ActionMenu({
  label,
  triggerContent,
  tone,
  placement = 'end',
  menuLabel,
  id,
  testId,
  children,
  onDialogOpened,
  search,
}: ActionMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const onSearchChange = search?.onChange;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /** ⛔ Cleared for exactly one close — see trap 3. Defaults back to `true` after it is consumed. */
  const restoreOnClose = useRef(true);
  /** ⛔ Trap 2: distinguishes a real open→close transition from the first render. */
  const prevOpen = useRef(false);
  /** The type-ahead text and the time of its last key. A pause longer than the reset starts a new text. */
  const typed = useRef<{ text: string; at: number }>({ text: '', at: 0 });

  useEffect(() => {
    const wasOpen = prevOpen.current;
    prevOpen.current = open;
    if (open && !wasOpen) {
      /* Each open starts a new type-ahead text, so a quick reopen does not extend the old one. */
      typed.current = { text: '', at: 0 };
      if (searchRef.current === null) focusMenuItem(menuRef.current, 0);
      else searchRef.current.focus();
      return;
    }
    if (!open && wasOpen) {
      onSearchChange?.('');
      if (restoreOnClose.current) focusPanel(triggerRef.current);
      restoreOnClose.current = true;
      /* A dialog that mounted in this commit has taken the offer already; see `closeForDialog`. */
      withdrawDialogOpener(triggerRef.current);
    }
  }, [open, onSearchChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocumentMouseDown = (event: MouseEvent): void => {
      if (isOutside(containerRef.current, event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  /**
   * ⛔ Passed DOWN to items so an item that opens a dialog can suppress the restore. Exposed as a
   * render-prop-free callback on purpose: threading it through `children` as a prop would require
   * cloning them, which trap-3's comment on {@link ActionMenuProps.children} rules out.
   *
   * It also offers the trigger as the opener of a `DialogPanel` that mounts in this commit, so the
   * dialog hands focus back to the trigger when it closes (R-063 part 5). The item that opened it
   * unmounts with the menu and cannot take focus back. The offer lasts one commit: the close effect
   * withdraws it when no dialog took it.
   */
  function closeForDialog(): void {
    restoreOnClose.current = false;
    offerDialogOpener(triggerRef.current);
    setOpen(false);
    onDialogOpened?.();
  }

  /** The close that {@link useCloseMenu} hands to an item: focus goes back to the trigger. */
  function closeMenu(): void {
    setOpen(false);
  }

  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    const action = classifyMenuKey(event.key);
    if (action === 'none') {
      /*
       * ⭐ **APG TYPE-AHEAD — the branch that lets a menu carry a list nobody wants to scroll.**
       * Measured 2026-08-07 against production: a `cp:app:list-all` operator sees **12** apps and
       * **9 of those 12 were created in the preceding 30 days**, over an unpaginated endpoint. A bare
       * menu is fine at 12 and a scroll-hunt at 30.
       *
       * ⛔ Only `preventDefault` on a key that MATCHED. A miss must stay inert rather than swallowing
       * the keystroke, so browser-level find-as-you-type and AT pass-through still work — and, per
       * {@link typeAheadIndex}, focus stays exactly where the operator left it.
       */
      const now = Date.now();
      const fresh = now - typed.current.at > TYPEAHEAD_RESET_MS;
      /* A space joins a text already under way, so "perf lab" matches; alone it still activates the item. */
      const joinsText = event.key === ' ' && !fresh;
      if (!isTypeAheadKey(event.key) && !joinsText) return;
      const text = fresh ? event.key : typed.current.text + event.key;
      typed.current = { text, at: now };
      const searchable = menuItemsIn(menuRef.current);
      const at = searchable.findIndex((item) => item === document.activeElement);
      const found = typeAheadIndex(searchable, text, at);
      if (found === -1) return;
      event.preventDefault();
      focusMenuItem(menuRef.current, found);
      return;
    }
    event.preventDefault();
    if (action === 'close') {
      setOpen(false);
      return;
    }
    const items = menuItemsIn(menuRef.current);
    /*
     * ⛔ Derived from the DOM's *current* focus rather than from a `roving` state value. The items here
     * are supplied by the caller and several are `aria-disabled` — a state counter would drift out of
     * agreement with the DOM the first time a state gate changed which items exist, and the symptom
     * would be arrow keys jumping two places.
     */
    const current = items.findIndex((item) => item === document.activeElement);
    /* With a field, the field sits in the cycle between the last row and the first. */
    const toField =
      ((action === 'prev' || action === 'first') && current <= 0) ||
      (action === 'next' && current === items.length - 1);
    if (search !== undefined && toField) {
      searchRef.current?.focus();
      return;
    }
    const from = current === -1 ? 0 : current;
    const target =
      action === 'next' ? from + 1 : action === 'prev' ? from - 1 : action === 'first' ? 0 : items.length - 1;
    focusMenuItem(menuRef.current, target);
  }

  function onSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    /* An IME uses Enter and Escape to end a composition, so those keys are not menu commands then. */
    if (isImeKey(event)) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusMenuItem(menuRef.current, 0);
    } else if (event.key === 'ArrowUp') {
      /* Index -1 wraps to the last row, so the cycle runs both ways. */
      event.preventDefault();
      focusMenuItem(menuRef.current, -1);
    } else if (event.key === 'Enter') {
      /* Enter chooses the first row left by the filter. With no match, Enter does nothing. */
      event.preventDefault();
      if (search?.empty !== true) menuItemsIn(menuRef.current)[0]?.click();
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  const menu = open ? (
    <div
      ref={menuRef}
      id={id}
      role="menu"
      aria-label={menuLabel}
      onKeyDown={onMenuKeyDown}
      data-testid={testId}
      className={search === undefined ? MENU_PLACEMENT_CLASS[placement] : SEARCH_MENU_CLASS}
    >
      <DialogCloseContext value={closeForDialog}>
        <MenuCloseContext value={closeMenu}>{children}</MenuCloseContext>
      </DialogCloseContext>
    </div>
  ) : null;

  return (
    <div
      ref={containerRef}
      className={TRIGGER_PLACEMENT_CLASS[placement]}
      data-testid={`${testId}-container`}
    >
      {/*
        ⛔ The `Button` PRIMITIVE, not a hand-rolled `<button className=…>`. Two ratchets count
        hand-rolled buttons — `one-button-definition.test.ts` for the shell and `primitive-adoption`'s
        `handRolledInPanes` for panes — and both may only be LOWERED, so a new one here would red a gate
        this change has no business touching. `Button` forwards `ref` and every `aria-*`/`data-*`.
      */}
      <Button
        ref={triggerRef}
        tone={tone}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          /*
           * ArrowDown opens the menu — the keyboard idiom for a menu button. The open effect puts focus
           * on the first item, or on the filter field when `search` is set. Enter and Space are the
           * button's own native activation and need no code.
           */
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          setOpen(true);
        }}
        /*
         * ⛔ `aria-haspopup="menu"` — and it is a FACT about what opens, so it may only be present
         * because a menu really opens. The rail chip shipped for one evening as a plain link that
         * correctly OMITTED this attribute and kept the chevron, which made it honest to a screen
         * reader and a lie to everyone else. ⇒ the two must move together.
         */
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        /* ⚠️ Only when the visible content is not the name — otherwise it would duplicate it. */
        aria-label={triggerContent === undefined ? undefined : label}
        data-testid={`${testId}-trigger`}
      >
        {triggerContent ?? label}
        {/* ⛔ No `aria-hidden` needed — lucide adds it itself; asserting it would be a vacuous test. */}
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>
      {/*
        ⛔ NO ENTRANCE ANIMATION, deliberately — so there is nothing for `prefers-reduced-motion` to
        gate. A menu is a response to a press and Doherty puts the bar at "instant".
      */}
      {search === undefined ? (
        menu
      ) : (
        open && (
          <div className={SEARCH_FRAME_PLACEMENT_CLASS[placement]} data-testid={`${testId}-frame`}>
            <div className="border-b border-border-subtle p-2">
              <input
                ref={searchRef}
                type="search"
                value={search.value}
                onChange={(event) => search.onChange(event.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={search.label}
                aria-label={search.label}
                aria-controls={id}
                autoComplete="off"
                data-testid={`${testId}-search`}
                className={SEARCH_FIELD_CLASS}
              />
              <p
                role="status"
                data-testid={`${testId}-empty`}
                className={search.empty ? 'px-1 pt-2 text-xs text-muted' : 'sr-only'}
              >
                {search.status}
              </p>
            </div>
            {menu}
          </div>
        )
      )}
    </div>
  );
}
